import "~/tailwind.css";

import { ApolloProvider as ApolloHooksProvider } from "@apollo/client";
import NiceModal from "@ebay/nice-modal-react";
import { TOAST_AUTO_CLOSE_DURATION } from "@shared/constants/index";
import Tooltip from "@shared/ui/Tooltip";
import mixpanel from "mixpanel-browser";
import React from "react";
import ReactDOM from "react-dom/client";
import {
  Navigate,
  Outlet,
  Route,
  RouterProvider,
  createBrowserRouter,
  createRoutesFromElements,
  useParams,
} from "react-router-dom";
import { ToastContainer } from "react-toastify";
import { IntercomProvider } from "react-use-intercom";
import ROUTES_SETTINGS from "$/settings/routes-resolvers.json";
import { apolloClient } from "~/apollo";
import App from "~/App";
import { Login } from "~/components";
import {
  ErrorBoundary,
  RootErrorElement,
  ErrorPageRoute,
  AuthPageGuard,
} from "~/components/general";
import ForgotPassword from "~/containers/_forgotPassword";
import ForgotPasswordSuccess from "~/containers/_forgotPasswordSuccess";
import AiNote from "~/containers/AiNote";
import ContentExplorer from "~/containers/box/ContentExplorer";
import Preview from "~/containers/box/Preview";
import MachineQRCode from "~/containers/MachineQRCode";
import LanguageProvider from "~/lang/provider";
import SignUp from "~/pages/SignUp/SignUp";
import { isMixpanelEnabled } from "~/utils/_mixpanel";

// index.css is imported via tailwind.css in the base layer
import PortalLanding from "./pages/PortalLanding/PortalLanding";
import WorkspaceAuthorization from "./pages/WorkspaceAuthorization/WorkspaceAuthorization";
import ResetPassword from "./pages/ResetPassword/ResetPassword";
import PublicQuotePage from "./pages/PublicQuote/PublicQuotePage";
import PublicInvoicePage from "./pages/PublicInvoice/PublicInvoicePage";

if (isMixpanelEnabled()) {
  mixpanel.init(import.meta.env.VITE_APP_MIXPANEL_TOKEN, {
    debug: true,
    api_host: "https://api-eu.mixpanel.com",
  });
}

//app level error listener for vite preload failure, which is caused by the chunk load failure. This is a workaround for the issue that the app will be stuck when the chunk load failure happens, and the only way to fix it is to reload the page.
window.addEventListener("vite:preloadError", () => {
  window.location.reload(true);
});

const { CLIENT: { ROUTES: { OEM_LOGIN: LOGIN } = {} } = {} } = ROUTES_SETTINGS;

const CustomerPortalRedirect = () => {
  const { slug } = useParams();
  return <Navigate to={slug ? `/workspace/${slug}` : "/workspace"} replace />;
};

const RootLayout = () => (
  <NiceModal.Provider>
    <Outlet />
    <ToastContainer
      className="toast-container message-container"
      toastClassName="message"
      draggable={false}
      hideProgressBar
      closeButton={false}
      autoClose={TOAST_AUTO_CLOSE_DURATION}
      position="bottom-right"
    />
    <Tooltip id="global-tooltip" />
  </NiceModal.Provider>
);

const router = createBrowserRouter(
  createRoutesFromElements(
    <Route element={<RootLayout />} errorElement={<RootErrorElement />}>
      <Route
        path={`${LOGIN}/:modifier?`}
        element={
          <AuthPageGuard>
            <Login />
          </AuthPageGuard>
        }
      />
      <Route
        path="/forgot-password/:modifier?"
        element={
          <AuthPageGuard>
            <ForgotPassword />
          </AuthPageGuard>
        }
      />
      <Route
        path="/customer-portal/:slug?"
        element={<CustomerPortalRedirect />}
      />
      <Route path="/workspace/:slug?" element={<PortalLanding />} />
      <Route path="/ai/note/:id" element={<AiNote />} />
      <Route path="/sign-up" element={<SignUp />} />
      <Route path="/box" element={<ContentExplorer />} />
      <Route path="/box/preview" element={<Preview />} />
      <Route
        path="/forgot-password-success/:modifier?"
        element={
          <AuthPageGuard>
            <ForgotPasswordSuccess />
          </AuthPageGuard>
        }
      />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/" element={<Navigate to="/app" replace />} />
      <Route
        path="/workspace/authorization/:id"
        element={<WorkspaceAuthorization />}
      />
      <Route path="/m/:machineUuid" element={<MachineQRCode />} />
      <Route path="/quotation/:oemSlug/:token" element={<PublicQuotePage />} />
      <Route path="/invoice/:oemSlug/:token" element={<PublicInvoicePage />} />
      <Route path="/app/*" element={<App />} />
      <Route path="/error-page/:error" element={<ErrorPageRoute />} />
      <Route path="*" element={<ErrorPageRoute />} />
    </Route>,
  ),
);

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <ApolloHooksProvider client={apolloClient}>
      <IntercomProvider
        appId={import.meta.env.VITE_APP_INTERCOM_APP_ID}
        autoBoot
      >
        <LanguageProvider>
          <ErrorBoundary>
            <RouterProvider router={router} />
          </ErrorBoundary>
        </LanguageProvider>
      </IntercomProvider>
    </ApolloHooksProvider>
  </React.StrictMode>,
);
